import { useLocation } from 'react-router-dom';
import JsonLd from './JsonLd';
import { getRouteSeo, buildCanonical } from '@/seo/siteSeo';
import {
  websiteSchema,
  organizationSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
  itemListSchema,
  webPageSchema,
} from '@/seo/schema';
import { articleContents } from '@/mocks/articleContent';
import { productCategories } from '@/mocks/products';

const siteFaq = [
  { question: 'What is CSSBuy Spreadsheet?', answer: 'An independent product discovery and comparison resource organized by category with updated product information and seller details.' },
  { question: 'Is CSSBuy Spreadsheet free?', answer: 'Yes, browsing and researching through the spreadsheet is free; you only pay when you order and ship items.' },
  { question: 'How much does CSSBuy shipping cost?', answer: 'Shipping cost depends on weight, dimensions, destination and method. Consolidating items can reduce your cost per item.' },
  { question: 'Is CSSBuy legit and safe?', answer: 'CSSBuy is a legitimate agent service, but safety depends on choosing reliable sellers and reviewing QC photos.' },
];

export default function SchemaManager() {
  const { pathname } = useLocation();
  const seo = getRouteSeo(pathname);

  if (!seo) return null;

  const url = buildCanonical(seo.path);
  const image = seo.image;
  const schemas: Record<string, unknown>[] = [];

  const homeBreadcrumb = [{ name: 'Home', item: buildCanonical('/') }];

  if (seo.schemaType === 'home') {
    schemas.push(websiteSchema());
    schemas.push(organizationSchema());
  } else if (seo.schemaType === 'article') {
    const slug = seo.path.replace(/^\//, '');
    const article = articleContents.find((a) => a.slug === slug);

    schemas.push(
      articleSchema({
        headline: seo.title,
        description: seo.description,
        image: image || '',
        url,
        datePublished: seo.datePublished || seo.dateModified || '2026-08-01',
        dateModified: seo.dateModified || seo.datePublished || '2026-08-27',
      }),
    );
    schemas.push(
      breadcrumbListSchema([
        ...homeBreadcrumb,
        { name: seo.title.length > 60 ? `${seo.title.slice(0, 60)}…` : seo.title, item: url },
      ]),
    );
    if (article && article.faq.length > 0) {
      schemas.push(faqPageSchema(article.faq));
    }
  } else if (seo.schemaType === 'products') {
    schemas.push(
      itemListSchema(
        seo.title,
        seo.description,
        url,
        productCategories,
      ),
    );
    schemas.push(
      breadcrumbListSchema([...homeBreadcrumb, { name: 'Products', item: url }]),
    );
  } else if (seo.schemaType === 'faq') {
    schemas.push(faqPageSchema(siteFaq));
    schemas.push(
      breadcrumbListSchema([...homeBreadcrumb, { name: 'FAQ', item: url }]),
    );
  } else {
    schemas.push(webPageSchema(seo.title, seo.description, url));
    schemas.push(
      breadcrumbListSchema([...homeBreadcrumb, { name: seo.title, item: url }]),
    );
  }

  return (
    <>
      {schemas.map((schema, index) => (
        <JsonLd key={index} data={schema} />
      ))}
    </>
  );
}